import { startCase } from "lodash";
import { P2Results } from "models/P2Results";
import { ITranscriptionSegment } from "services/TranscriptionService";
import { useState, useLayoutEffect } from "react";
import { Blurb, ChatBlurb } from "./Components/BlurbBox/Blurb";
import { IChatRecord } from "../Chat/AcxChatViewer";

export const convertTranscriptToBlurbs = (
    transcriptWordsOrPhrases: ITranscriptionSegment[],
    eddyP2Data?: P2Results[],
    adverseEventP2Data?: P2Results[],
    isPhrasesTx?: boolean,
): Blurb[] => {
    const newBlurbs: Blurb[] = [];
    if (
        !transcriptWordsOrPhrases ||
        transcriptWordsOrPhrases[0]?.channel === undefined
    ) {
        return newBlurbs;
    }

    let currentChannel = transcriptWordsOrPhrases[0].channel;
    let blurbText = "";
    let wordArray: ITranscriptionSegment[] = [];
    let foundEddyWord: boolean = false;
    let foundAdverseWord: boolean = false;
    let firstWordIndex = 0;

    let segmentsToUse = transcriptWordsOrPhrases;

    if (isPhrasesTx) {
        // need to format phrase based tx to have only 1 word per segment
        let formattedSegmentsFromPhraseTx = transcriptWordsOrPhrases.flatMap(
            (seg) => {
                const newSegsWith1Word = seg.text.split(" ").map((word) => {
                    return {
                        text: word,
                        channel: seg.channel,
                        startTime: seg.startTime,
                        endTime: seg.endTime,
                    } as ITranscriptionSegment;
                });

                return newSegsWith1Word;
            },
        );
        segmentsToUse = formattedSegmentsFromPhraseTx;
    }

    for (let i = 0; i < segmentsToUse.length; i++) {
        const currentWord = segmentsToUse[i];
        // startIndex 0 and endIndex 1 will result in only the first word being counted
        // startIndex 0 and endIndex 2 will result in first two words and so on. It's exclusive (cuz the p2 results are exclusive)
        foundEddyWord =
            eddyP2Data?.some((item: P2Results) => {
                return i >= item.startWordIndex && i < item.endWordIndex;
            }) || foundEddyWord;
        foundAdverseWord =
            adverseEventP2Data?.some((item: P2Results) => {
                return i >= item.startWordIndex && i < item.endWordIndex;
            }) || foundAdverseWord;
        if (currentWord.channel === currentChannel) {
            blurbText += `${currentWord.text} `;
            wordArray.push(currentWord);
        } else {
            newBlurbs.push(
                new Blurb(
                    blurbText,
                    currentChannel,
                    [
                        wordArray[0]?.startTime,
                        wordArray[wordArray.length - 1]?.endTime,
                    ],
                    foundEddyWord,
                    foundAdverseWord,
                    firstWordIndex,
                    i - 1, // last word index
                ),
            );
            foundEddyWord = false;
            foundAdverseWord = false;
            firstWordIndex = i;

            blurbText = `${currentWord.text} `;
            currentChannel = currentWord.channel;
            wordArray = [currentWord];
        }
    }

    if (wordArray.length > 0) {
        newBlurbs.push(
            new Blurb(
                blurbText,
                currentChannel,
                [
                    wordArray[0]?.startTime,
                    wordArray[wordArray.length - 1]?.endTime,
                ],
                foundEddyWord,
                foundAdverseWord,
                firstWordIndex,
                segmentsToUse.length - 1, // last word index
            ),
        );
    }

    return newBlurbs;
};

export const convertChatToBlurbs = (
    chatRecords: IChatRecord[],
    eddyData?: P2Results[],
    adverseData?: P2Results[],
): ChatBlurb[] => {
    const newBlurbs: ChatBlurb[] = [];
    if (!chatRecords || chatRecords.length === 0) {
        return newBlurbs;
    }

    let currentPersona = chatRecords[0].from;
    let blurbText = "";
    let wordArray: IChatRecord[] = [];
    let foundEddyWord: boolean = false;
    let foundAdverseWord: boolean = false;
    let firstWordIndex = 0;

    // need to format phrase based tx to have only 1 word per segment
    let chatWords = chatRecords.flatMap((seg) => {
        if (!seg.text) return [];
        const newSegsWith1Word = seg.text.split(" ").map((word) => {
            return {
                text: word,
                from: seg.from,
                timestamp: seg.timestamp,
            } as IChatRecord;
        });

        return newSegsWith1Word;
    });

    for (let i = 0; i < chatWords.length; i++) {
        // for phrase based tx, this is more than 1 word
        const currentWord = chatWords[i];

        foundEddyWord =
            eddyData?.some((item: P2Results) => {
                return i >= item.startWordIndex && i < item.endWordIndex;
            }) ?? false;
        foundAdverseWord =
            adverseData?.some((item: P2Results) => {
                return i >= item.startWordIndex && i < item.endWordIndex;
            }) ?? false;
        if (currentWord.from === currentPersona) {
            blurbText += `${currentWord.text} `;
            wordArray.push(currentWord);
        } else {
            newBlurbs.push({
                text: blurbText,
                from: currentPersona,
                timestamp: currentWord.timestamp,
                foundEddy: foundEddyWord,
                foundAdverse: foundAdverseWord,
                firstWordIndex: firstWordIndex,
                lastWordIndex: i - 1,
            });

            blurbText = `${currentWord.text} `;
            currentPersona = currentWord.from;
            wordArray = [currentWord];
            firstWordIndex = i;
        }
    }

    if (wordArray.length > 0) {
        newBlurbs.push({
            text: blurbText,
            from: currentPersona,
            timestamp: wordArray[0].timestamp,
            foundEddy: foundEddyWord,
            foundAdverse: foundAdverseWord,
            firstWordIndex: firstWordIndex,
            lastWordIndex: chatWords.length - 1,
        });
    }

    return newBlurbs;
};

export const useWindowResize = () => {
    const [size, setSize] = useState([0, 0]);

    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener("resize", updateSize);
        updateSize();

        return () => window.removeEventListener("resize", updateSize);
    }, []);

    return size;
};

export const formatSegmentHeading = (heading: string): string => {
    if (heading.toLowerCase().includes("problem_statement"))
        return "Problem Statement";
    if (heading.toLowerCase().includes("clarification")) return "Clarification";
    if (heading.toLowerCase().includes("ivr")) return "IVR";
    return heading
        .split("/")
        .map((part) => startCase(part.toLowerCase()))
        .join(" / ");
};
